import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import rvgLogo from '../../assets/RVG Logo.png';

/**
 * NavigationShell - Persistent header with responsive mobile menu and smooth animations
 * Respects prefers-reduced-motion via Framer Motion's built-in support
 */
function NavigationShell({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'About Us', path: '/about' },
    { label: 'Our Projects', path: '/projects' },
    { label: 'View Live Action', path: '/live-dashboard' },
    { label: 'Contact Us', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const mobileMenuVariants = {
    closed: { opacity: 0, x: '100%' },
    open: { opacity: 1, x: 0 },
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <nav className="container mx-auto px-4 py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 text-2xl font-bold text-primary focus-visible-ring"
              aria-label="Real Value Group Home"
            >
              <img
                src={rvgLogo}
                alt="Real Value Group Logo"
                className="h-10 w-auto"
              />
              <span>Real Value Group</span>
            </Link>

            {/* Desktop Navigation */}
            <ul className="hidden md:flex gap-6 items-center">
              {menuItems.map((item) => (
                <motion.li 
                  key={item.path}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={item.path}
                    className={`
                      px-3 py-2 rounded-md text-sm font-medium transition-all duration-300
                      focus-visible-ring
                      ${
                        isActive(item.path)
                          ? 'text-white bg-[#0472ab] shadow-sm'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50 hover:shadow-md'
                      }
                    `}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                </motion.li>
              ))}
            </ul>

            {/* Mobile Menu Button */}
            <button
              type="button"
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus-visible-ring"
              onClick={toggleMobileMenu}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="md:hidden fixed inset-y-0 right-0 w-64 bg-white shadow-2xl z-50"
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              transition={{ 
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }}
            >
              <div className="p-4">
                <button
                  type="button"
                  className="mb-4 p-2 rounded-md text-gray-700 hover:bg-gray-100 focus-visible-ring"
                  onClick={toggleMobileMenu}
                  aria-label="Close mobile menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <motion.ul 
                  className="space-y-2"
                  initial="closed"
                  animate="open"
                  variants={{
                    open: {
                      transition: { staggerChildren: 0.07, delayChildren: 0.1 }
                    },
                    closed: {
                      transition: { staggerChildren: 0.05, staggerDirection: -1 }
                    }
                  }}
                >
                  {menuItems.map((item) => (
                    <motion.li 
                      key={item.path}
                      variants={{
                        open: {
                          y: 0,
                          opacity: 1,
                          transition: {
                            y: { stiffness: 1000, velocity: -100 }
                          }
                        },
                        closed: {
                          y: 20,
                          opacity: 0,
                          transition: {
                            y: { stiffness: 1000 }
                          }
                        }
                      }}
                    >
                      <Link
                        to={item.path}
                        className={`
                          block px-4 py-3 rounded-md text-base font-medium transition-all duration-300
                          focus-visible-ring
                          ${
                            isActive(item.path)
                              ? 'text-white bg-[#0472ab] shadow-sm'
                              : 'text-gray-700 hover:text-primary hover:bg-gray-50 hover:pl-6'
                          }
                        `}
                        onClick={() => setMobileMenuOpen(false)}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                      >
                        {item.label}
                      </Link>
                    </motion.li>
                  ))}
                </motion.ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Backdrop */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="md:hidden fixed inset-0 bg-black z-40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={toggleMobileMenu}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Real Value Group</h3>
              <p className="text-sm text-gray-600">
                Turning your business data into actionable intelligence
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact</h3>
              <address className="not-italic text-sm text-gray-600 space-y-1">
                <p>Bergstraat</p>
                <p>22/303, 3110</p>
                <p>Rotselaar, Belgium </p>
                <p>
                  <a
                    href="mailto:info@realvaluegroup.io"
                    className="text-primary hover:underline focus-visible-ring"
                  >
                    info@realvaluegroup.io
                  </a>
                </p>
              </address>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Follow Us</h3>
              <a
                href="https://linkedin.com/company/realvaluegroup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-primary hover:underline focus-visible-ring"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
                LinkedIn
              </a>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t border-gray-200 text-center text-sm text-gray-600">
            <p>&copy; {new Date().getFullYear()} Real Value Group. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
}

export default NavigationShell;

